import { useMemo, useState, type ReactNode } from 'react';
import {
  Check, Copy, KeyRound, Pencil, Plus, Shield, ShieldCheck, Trash2, Users as UsersIcon, UserRound,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { api, toApiError } from '@/lib/api';
import { useInvalidate, useRoles, useUsers } from '@/lib/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/Toast';
import { PAGE_GROUPS, type PageGroup } from '@/lib/permissions';
import type { PageDef, RoleItem, User } from '@/lib/types';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Field, Input } from '@/components/ui/Field';
import { Modal, Select, Switch, Tooltip } from '@/components/ui/primitives';
import { EmptyState, Notice, TableSkeleton } from '@/components/ui/Feedback';

/** Gated on the 'users' page permission — a user without it never gets here. */
export function Settings() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [tab, setTab] = useState<'users' | 'roles'>('users');

  if (!can('users')) {
    return (
      <div className="mx-auto max-w-content px-6 py-16">
        <EmptyState icon={<ShieldCheck className="h-6 w-6" />} title={t.errors.forbidden} hint={t.errors.forbiddenHint} />
      </div>
    );
  }

  const tabs = [
    { key: 'users' as const, label: t.users.title, icon: UsersIcon },
    { key: 'roles' as const, label: t.roles.title, icon: Shield },
  ];

  return (
    <>
      <header className="px-4 pt-6 sm:px-6">
        <div className="mx-auto max-w-content">
          <h1 className="font-display text-title text-strong">{t.nav.settings}</h1>
          <nav className="mt-5 flex gap-7 border-b border-line" aria-label={t.nav.settings}>
            {tabs.map((item) => {
              const active = tab === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'relative flex cursor-pointer items-center gap-2 pb-3 text-body font-medium',
                    'transition-ui duration-instant',
                    active ? 'text-strong' : 'text-muted hover:text-strong',
                  )}
                >
                  <item.icon className="h-5 w-5" strokeWidth={2} />
                  {item.label}
                  {active && (
                    <span aria-hidden className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-brand-500" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>
      <div className="pt-6">{tab === 'users' ? <Users /> : <Roles />}</div>
    </>
  );
}

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
}

/** A row-level action: a 36px ghost square with a clear 19px glyph. */
function IconButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'default',
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}) {
  return (
    <Tooltip label={label} side="top">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={cn(
          'inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-control text-muted',
          'transition-ui duration-instant active:scale-[0.96]',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100',
          tone === 'danger'
            ? 'hover:bg-accent-500/10 hover:text-accent-600 dark:hover:text-accent-400'
            : 'hover:bg-raised hover:text-strong',
        )}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

// ── Users ──────────────────────────────────────────────────────────────────

function Users() {
  const { t, formatRelative } = useI18n();
  const { user: me } = useAuth();
  const toast = useToast();
  const invalidate = useInvalidate();
  const { data, isLoading } = useUsers();
  const { data: rolesData } = useRoles();
  const roles = rolesData?.items ?? [];

  const [dialog, setDialog] = useState<User | 'new' | null>(null);
  const [issued, setIssued] = useState<{ user: string; password: string } | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);

  const items = data?.items ?? [];

  return (
    <div className="mx-auto w-full max-w-content px-6 pb-14">
      <div className="mb-3 flex items-center justify-end">
        <Button
          variant="primary"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setDialog('new')}
          disabled={roles.length === 0}
        >
          {t.users.new}
        </Button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : items.length === 0 ? (
          <EmptyState icon={<UserRound className="h-6 w-6" />} title={t.users.title} />
        ) : (
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-line">
                <th className="px-5 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-faint">
                  {t.users.name}
                </th>
                <th className="hidden px-3 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-faint sm:table-cell">
                  {t.users.role}
                </th>
                <th className="hidden px-3 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-faint md:table-cell">
                  {t.users.lastLogin}
                </th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((user) => (
                <tr key={user.id} className="group transition-ui duration-instant hover:bg-raised/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-chip font-display text-[0.75rem] font-bold',
                          user.isActive === false
                            ? 'bg-line text-faint'
                            : 'bg-brand-500/10 text-brand-600 dark:text-brand-300',
                        )}
                      >
                        {initials(user.name)}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={cn('truncate text-body font-medium', user.isActive === false ? 'text-faint line-through' : 'text-strong')}>
                            {user.name}
                          </span>
                          {user.mustChangePassword && (
                            <Tooltip label={t.users.issuedPassword} side="top">
                              <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                            </Tooltip>
                          )}
                        </div>
                        <span className="block truncate font-mono text-[0.75rem] text-faint">
                          {user.username ?? user.email ?? '—'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-3 py-3 sm:table-cell">
                    <Chip
                      tone={user.role?.isSuperAdmin ? 'brand' : 'neutral'}
                      icon={user.role?.isSuperAdmin ? <ShieldCheck className="h-3 w-3" strokeWidth={2} /> : undefined}
                    >
                      {user.role?.name ?? t.users.noRole}
                    </Chip>
                  </td>
                  <td className="hidden px-3 py-3 text-ui text-faint md:table-cell">
                    {user.lastLoginAt ? formatRelative(user.lastLoginAt) : t.users.neverLoggedIn}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-0.5">
                      <IconButton
                        icon={<Pencil className="h-[1.1875rem] w-[1.1875rem]" strokeWidth={1.9} />}
                        label={t.common.edit}
                        onClick={() => setDialog(user)}
                      />
                      <IconButton
                        icon={<KeyRound className="h-[1.1875rem] w-[1.1875rem]" strokeWidth={1.9} />}
                        label={t.users.resetPassword}
                        onClick={() => setResetting(user)}
                        disabled={user.id === me?.id}
                      />
                      <IconButton
                        tone="danger"
                        icon={<Trash2 className="h-[1.1875rem] w-[1.1875rem]" strokeWidth={1.9} />}
                        label={user.id === me?.id ? t.users.deleteSelf : t.users.delete}
                        onClick={() => setDeleting(user)}
                        disabled={user.id === me?.id}
                      />
                      <Tooltip label={user.isActive !== false ? t.users.disable : t.users.enable} side="top">
                        <span className="ml-1.5 inline-flex">
                          <Switch
                            checked={user.isActive !== false}
                            onCheckedChange={async (checked) => {
                              try {
                                await api.patch(`/users/${user.id}`, { isActive: checked });
                                void invalidate(['users']);
                              } catch (error) {
                                toast({ tone: 'error', message: toApiError(error).message });
                              }
                            }}
                          />
                        </span>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialog && (
        <UserDialog
          user={dialog === 'new' ? null : dialog}
          roles={roles}
          onClose={() => setDialog(null)}
          onCreated={(user, password) => {
            void invalidate(['users']);
            void invalidate(['roles']);
            setDialog(null);
            setIssued({ user: user.name, password });
          }}
          onUpdated={() => {
            void invalidate(['users']);
            void invalidate(['roles']);
            setDialog(null);
          }}
        />
      )}

      <Modal
        open={Boolean(resetting)}
        onOpenChange={(open) => !open && setResetting(null)}
        title={t.users.resetPassword}
        description={resetting?.name}
        footer={
          <>
            <Button variant="outline" onClick={() => setResetting(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (!resetting) return;
                try {
                  const { data: result } = await api.post<{ issuedPassword: string }>(
                    `/users/${resetting.id}/reset-password`,
                  );
                  setIssued({ user: resetting.name, password: result.issuedPassword });
                  setResetting(null);
                  void invalidate(['users']);
                } catch (error) {
                  toast({ tone: 'error', message: toApiError(error).message });
                }
              }}
            >
              {t.users.resetPassword}
            </Button>
          </>
        }
      >
        <p className="text-ui leading-relaxed text-muted">{t.users.resetPasswordBody}</p>
      </Modal>

      <Modal
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t.users.delete}
        description={deleting?.name}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!deleting) return;
                try {
                  await api.delete(`/users/${deleting.id}`);
                  void invalidate(['users']);
                  void invalidate(['roles']);
                  setDeleting(null);
                } catch (error) {
                  toast({ tone: 'error', message: toApiError(error).message });
                }
              }}
            >
              {t.users.delete}
            </Button>
          </>
        }
      >
        <p className="text-ui leading-relaxed text-muted">{t.users.deleteBody}</p>
      </Modal>

      <IssuedPasswordModal issued={issued} onClose={() => setIssued(null)} />
    </div>
  );
}

function IssuedPasswordModal({
  issued,
  onClose,
}: {
  issued: { user: string; password: string } | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  return (
    <Modal
      open={Boolean(issued)}
      onOpenChange={(open) => !open && onClose()}
      title={t.users.issuedPassword}
      description={issued?.user}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t.common.close}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <code className="flex-1 select-all rounded-control border border-line bg-raised px-4 py-3 font-mono text-[1.0625rem] tracking-wide text-strong">
            {issued?.password}
          </code>
          <Button
            variant="outline"
            icon={<Copy className="h-4 w-4" />}
            aria-label={t.common.copy}
            onClick={() => {
              if (issued) void navigator.clipboard.writeText(issued.password);
              toast({ tone: 'info', message: t.common.copied });
            }}
          />
        </div>
        <Notice tone="warn">{t.users.issuedPasswordHint}</Notice>
      </div>
    </Modal>
  );
}

function UserDialog({
  user,
  roles,
  onClose,
  onCreated,
  onUpdated,
}: {
  user: User | null;
  roles: RoleItem[];
  onClose: () => void;
  onCreated: (user: User, password: string) => void;
  onUpdated: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const editing = Boolean(user);
  const [name, setName] = useState(user?.name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState(
    () => user?.roleId ?? (roles.find((r) => !r.isSuperAdmin) ?? roles[0])?.id ?? '',
  );
  const [busy, setBusy] = useState(false);

  const usernameOk = /^[a-z0-9._-]{3,40}$/.test(username.trim().toLowerCase());
  const passwordOk = editing || password.length >= 10;
  const valid = name.trim().length >= 2 && usernameOk && passwordOk && roleId;

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={editing ? t.common.edit : t.users.new}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!valid}
            onClick={async () => {
              setBusy(true);
              try {
                if (editing && user) {
                  await api.patch(`/users/${user.id}`, {
                    name: name.trim(),
                    username: username.trim().toLowerCase(),
                    roleId,
                  });
                  onUpdated();
                } else {
                  const { data } = await api.post<{ item: User; issuedPassword: string }>('/users', {
                    name: name.trim(),
                    username: username.trim().toLowerCase(),
                    password,
                    roleId,
                  });
                  onCreated(data.item, data.issuedPassword);
                }
              } catch (error) {
                toast({ tone: 'error', message: toApiError(error).message });
              } finally {
                setBusy(false);
              }
            }}
          >
            {editing ? t.common.save : t.common.create}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label={t.users.name}>
            {({ id }) => <Input id={id} autoFocus value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t.users.username}>
            {({ id }) => (
              <Input
                id={id}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            )}
          </Field>
        </div>
        {!editing && (
          <Field label={t.users.password}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
        )}
        <Field label={t.users.role}>
          {({ id }) => (
            <Select
              id={id}
              value={roleId}
              onValueChange={setRoleId}
              options={roles.map((role) => ({ value: role.id, label: role.name }))}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}

// ── Roles ────────────────────────────────────────────────────────────────

function Roles() {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const { data, isLoading } = useRoles();
  const roles = data?.items ?? [];
  const pages = data?.pages ?? [];

  const [editing, setEditing] = useState<RoleItem | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RoleItem | null>(null);

  return (
    <div className="mx-auto w-full max-w-content px-6 pb-14">
      <div className="mb-3 flex items-center justify-end">
        <Button variant="primary" size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          {t.roles.new}
        </Button>
      </div>

      {isLoading ? (
        <div className="card overflow-hidden">
          <TableSkeleton rows={3} cols={3} />
        </div>
      ) : roles.length === 0 ? (
        <div className="card">
          <EmptyState icon={<Shield className="h-6 w-6" />} title={t.roles.empty} />
        </div>
      ) : (
        <div className="card divide-y divide-line overflow-hidden">
          {roles.map((role) => (
            <div key={role.id} className="group flex items-center gap-4 px-5 py-4 transition-ui duration-instant hover:bg-raised/50">
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-chip',
                  role.isSuperAdmin ? 'bg-brand-500/12 text-brand-600 dark:text-brand-300' : 'bg-raised text-muted',
                )}
              >
                {role.isSuperAdmin ? <ShieldCheck className="h-4 w-4" strokeWidth={2} /> : <Shield className="h-4 w-4" strokeWidth={1.85} />}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-body font-semibold text-strong">{role.name}</span>
                  {role.isSystem && !role.isSuperAdmin && <Chip tone="neutral">{t.roles.system}</Chip>}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {role.isSuperAdmin ? (
                    <span className="text-[0.75rem] text-muted">{t.roles.superAdminNote}</span>
                  ) : role.permissions.length === 0 ? (
                    <span className="text-[0.75rem] text-faint">—</span>
                  ) : (
                    role.permissions.map((key) => (
                      <span
                        key={key}
                        className="rounded-chip bg-raised px-2 py-0.5 text-[0.6875rem] font-medium text-muted ring-1 ring-inset ring-line"
                      >
                        {t.pageLabels[key] ?? key}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <span className="hidden shrink-0 whitespace-nowrap text-ui text-faint sm:block">
                {t.roles.usersCount(role.userCount)}
              </span>

              <div className="flex shrink-0 items-center gap-0.5">
                {/* Every role is editable except the super admin (its access is
                    fixed). Only non-system roles can be deleted. */}
                {!role.isSuperAdmin && (
                  <IconButton
                    icon={<Pencil className="h-[1.1875rem] w-[1.1875rem]" strokeWidth={1.9} />}
                    label={t.common.edit}
                    onClick={() => setEditing(role)}
                  />
                )}
                {!role.isSystem && (
                  <IconButton
                    tone="danger"
                    icon={<Trash2 className="h-[1.1875rem] w-[1.1875rem]" strokeWidth={1.9} />}
                    label={role.userCount > 0 ? t.roles.deleteHasUsers : t.roles.delete}
                    onClick={() => setDeleting(role)}
                    disabled={role.userCount > 0}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RoleDialog
          role={editing === 'new' ? null : editing}
          pages={pages}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void invalidate(['roles']);
            setEditing(null);
          }}
        />
      )}

      <Modal
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t.roles.delete}
        description={deleting?.name}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!deleting) return;
                try {
                  await api.delete(`/roles/${deleting.id}`);
                  void invalidate(['roles']);
                  setDeleting(null);
                } catch (error) {
                  toast({ tone: 'error', message: toApiError(error).message });
                }
              }}
            >
              {t.roles.delete}
            </Button>
          </>
        }
      >
        <p className="text-ui leading-relaxed text-muted">{t.roles.deleteBody}</p>
      </Modal>
    </div>
  );
}

function RoleDialog({
  role,
  pages,
  onClose,
  onSaved,
}: {
  role: RoleItem | null;
  pages: PageDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(role?.permissions ?? []));
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const groups: Record<PageGroup, PageDef[]> = { app: [], content: [], manage: [], system: [] };
    for (const page of pages) groups[page.group as PageGroup]?.push(page);
    return groups;
  }, [pages]);

  const groupLabel: Record<PageGroup, string> = {
    app: t.nav.groupApp,
    content: t.nav.groupContent,
    manage: t.nav.groupManage,
    system: t.nav.groupSystem,
  };

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allKeys = pages.map((p) => p.key);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={role ? t.roles.edit : t.roles.new}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={name.trim().length < 2}
            onClick={async () => {
              setBusy(true);
              try {
                const body = {
                  name: name.trim(),
                  description: description.trim() || null,
                  permissions: [...selected],
                };
                if (role) await api.patch(`/roles/${role.id}`, body);
                else await api.post('/roles', body);
                onSaved();
              } catch (error) {
                toast({ tone: 'error', message: toApiError(error).message });
              } finally {
                setBusy(false);
              }
            }}
          >
            {role ? t.common.save : t.common.create}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label={t.roles.name}>
            {({ id }) => <Input id={id} autoFocus value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t.roles.description}>
            {({ id }) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
          </Field>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-ui font-medium text-strong">{t.roles.permissions}</span>
            <button
              type="button"
              onClick={() => setSelected(allSelected ? new Set() : new Set(allKeys))}
              className="cursor-pointer text-ui font-medium text-brand-600 transition-ui duration-instant hover:text-brand-700 dark:text-brand-300"
            >
              {allSelected ? t.roles.clearAll : t.roles.selectAll}
            </button>
          </div>

          <div className="flex flex-col gap-3.5">
            {PAGE_GROUPS.map((group) => {
              const groupItems = grouped[group];
              if (!groupItems || groupItems.length === 0) return null;
              return (
                <div key={group} className="flex flex-col gap-1.5">
                  <span className="text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-faint/80">
                    {groupLabel[group]}
                  </span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {groupItems.map((page) => {
                      const on = selected.has(page.key);
                      return (
                        <button
                          key={page.key}
                          type="button"
                          onClick={() => toggle(page.key)}
                          aria-pressed={on}
                          className={cn(
                            'flex items-center gap-2.5 rounded-control border px-3 py-2 text-left text-ui transition-ui duration-instant',
                            on
                              ? 'border-brand-500/40 bg-brand-500/[0.07] text-strong dark:bg-brand-500/[0.14]'
                              : 'border-line text-muted hover:border-line-strong hover:bg-raised',
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition-ui duration-instant',
                              on ? 'border-brand-500 bg-brand-500 text-white' : 'border-line-strong',
                            )}
                          >
                            {on && <Check className="h-3 w-3" strokeWidth={3} />}
                          </span>
                          <span className="truncate font-medium">{t.pageLabels[page.key] ?? page.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

